import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG = {
  distributor: {
    submit: "Submit Distributor Enquiry",
    success: "Distributor enquiry received. Our sales team will call you shortly.",
    messageLabel: "Tell us about your distribution network",
    showCity: true,
  },
  export: {
    submit: "Submit Export Enquiry",
    success: "Export enquiry received. Our exports desk will respond within 24 hours.",
    messageLabel: "Products, volumes & destination port",
    showCountry: true,
  },
  contact: {
    submit: "Send Message",
    success: "Message sent. We'll get back to you soon.",
    messageLabel: "Your message",
  },
  career: {
    submit: "Submit Application",
    success: "Application received. Our HR team will reach out if there's a fit.",
    messageLabel: "Role you're interested in & your experience",
    showCity: true,
  },
};

const fieldCls =
  "bg-black/50 border-white/10 text-cream placeholder:text-zinc-600 rounded-sm h-12 focus-visible:ring-gold focus-visible:border-gold/60 transition-colors duration-300";

export default function InquiryForm({ type = "contact", testId }) {
  const cfg = CONFIG[type];
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = Array.isArray(data.detail)
          ? data.detail.map((d) => d.msg).join(" ")
          : data.detail || "Something went wrong. Please try again.";
        throw new Error(detail);
      }
      toast.success(cfg.success);
      setForm({ name: "", company: "", email: "", phone: "", country: "", city: "", message: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={testId || `${type}-inquiry-form`} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor={`${type}-name`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">Full Name *</Label>
          <Input id={`${type}-name`} data-testid={`${type}-form-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${type}-company`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">
            {type === "career" ? "Current City / Town" : "Company / Firm"}
          </Label>
          <Input id={`${type}-company`} data-testid={`${type}-form-company-input`} value={form.company} onChange={set("company")} placeholder={type === "career" ? "e.g. Barwani" : "Business name"} className={fieldCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor={`${type}-email`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">Email *</Label>
          <Input id={`${type}-email`} data-testid={`${type}-form-email-input`} type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${type}-phone`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">Phone / WhatsApp *</Label>
          <Input id={`${type}-phone`} data-testid={`${type}-form-phone-input`} required minLength={6} value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={fieldCls} />
        </div>
      </div>

      {(cfg.showCountry || cfg.showCity) && (
        <div className="grid sm:grid-cols-2 gap-5">
          {cfg.showCountry && (
            <div className="space-y-2">
              <Label htmlFor={`${type}-country`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">Country *</Label>
              <Input id={`${type}-country`} data-testid={`${type}-form-country-input`} required value={form.country} onChange={set("country")} placeholder="Destination country" className={fieldCls} />
            </div>
          )}
          {cfg.showCity && (
            <div className="space-y-2">
              <Label htmlFor={`${type}-city`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">City / Territory *</Label>
              <Input id={`${type}-city`} data-testid={`${type}-form-city-input`} required value={form.city} onChange={set("city")} placeholder="Your city or territory" className={fieldCls} />
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${type}-message`} className="text-xs uppercase tracking-[0.16em] text-zinc-400">{cfg.messageLabel}</Label>
        <Textarea
          id={`${type}-message`}
          data-testid={`${type}-form-message-input`}
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Write here..."
          className="bg-black/50 border-white/10 text-cream placeholder:text-zinc-600 rounded-sm focus-visible:ring-gold focus-visible:border-gold/60 transition-colors duration-300 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid={`${type}-form-submit-button`}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-sm transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(212,175,55,0.35)] hover:bg-gold-light disabled:opacity-60 disabled:translate-y-0"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {loading ? "Sending..." : cfg.submit}
      </button>
    </form>
  );
}
